import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatisticsByReferralFilter from '../StatisticsByReferralFilter';
import StatisticsByReferralTableComponent from '../StatisticsByReferralTable/StatisticsByReferralTable.container';
import StatisticsByReferralMetricsComponent from '../StatisticsByReferralMetrics/StatisticsByReferralMetrics.container';
import StatisticsByReferralSegmentsComponent from '../StatisticsByReferralSegments/StatisticsByReferralSegments.container';
import Pagination from '../../../Pagination';
import StatisticsByReferralCSV from '../StatisticsByReferralCSV/StatisticsByReferralCSV.container';
import config from '../../../../config';
import objectHasChanged from '../../../../helpers/objectHasChanged';
import query from '../../../../helpers/query';
import ReportByReferralComponent from '../../../../utils/ReportByReferralComponent/ReportByReferralComponent';
import Loading from '../../../Loading';
import { isSameFilterValues } from '../../../../helpers/reportFilterReset';

const reportUtils = ReportByReferralComponent.utils;

const defDataSort = {
    sort: 'GroupBy',
    order: 'desc'
};

export class StatisticsByReferralContent extends React.Component {
    constructor(props) {
        super();

        const parsedQuery = query.parse((props.location && props.location.query) || {});
        this.hasQueryMetrics = Boolean(parsedQuery.metrics);
        this.defaultMetrics = reportUtils.getDefStrMetric();
        const initialMetrics = parsedQuery.metrics || props.metrics || '';

        let startDate = this.getDateFromString(parsedQuery.startDate);
        if (startDate) {
            startDate = startDate.startOf('day');
        } else {
            startDate = moment().subtract(6, 'days').startOf('day');

        }

        let endDate = this.getDateFromString(parsedQuery.endDate);
        if (endDate) {
            endDate = endDate.endOf('day');
        } else {
            endDate = moment().endOf('day');
        }

        this.state = {
            startDate: startDate,
            endDate:  endDate,
            bindActions: parsedQuery.bindActions || config.defaultBindActions,
            groupBy: parsedQuery.groupBy || config.defaultGroupByForReferral,
            count: parsedQuery.count || config.defaultCount,
            metrics: initialMetrics,
            sort: parsedQuery.sort || defDataSort.sort,
            order: parsedQuery.order || defDataSort.order,
            segments: parsedQuery.segments || props.segments || {},
            page: parsedQuery.page || +props.page,
            totalPages: props.totalPages,
            loadingData: props.loadingData
        };

        this.isStateSearchChanged = objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, this.state);

        this.onActionFilter = this.onActionFilter.bind(this);
        this.onActionTable = this.onActionTable.bind(this);
        this.onActionMetrics = this.onActionMetrics.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onActionSegments = this.onActionSegments.bind(this);
        this.onUpdateDate = this.onUpdateDate.bind(this);
        this.onResetAll = this.onResetAll.bind(this);

        // skips the echo of a reset from the top filter (see helpers/reportFilterReset)
        this.resetEcho = null;
    }

    componentDidMount() {
        this.syncMetrics(this.props, true);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.totalPages !== this.props.totalPages || prevProps.loadingData !== this.props.loadingData) {
            this.setState({
                totalPages: this.props.totalPages,
                loadingData: this.props.loadingData
            });
        }

        if (
            prevProps.metrics !== this.props.metrics ||
            prevProps.serverStorageReady !== this.props.serverStorageReady
        ) {
            this.syncMetrics(this.props, false);
        }
    }

    syncMetrics(props, isMount) {
        if (this.hasQueryMetrics) {
            return;
        }

        const { metrics: propMetrics, serverStorageReady } = props;
        const currentMetrics = this.state.metrics;

        if (propMetrics && propMetrics !== currentMetrics) {
            this.setState({
                metrics: propMetrics
            });
            return;
        }

        if (serverStorageReady && !currentMetrics) {
            this.setState({
                metrics: this.defaultMetrics
            });
            return;
        }
    }

    getDateFromString(date) {
        if (date) {
            date = new Date(date);

            return moment().set({'year': date.getFullYear(), 'month': date.getMonth(), 'date': date.getDate()});
        }
        return false;
    }

    pushQuery() {
        query.replace({
            startDate: this.state.startDate.startOf('day').format(config.reportHourFormat),
            endDate: this.state.endDate.endOf('day').format(config.reportHourFormat),
            groupBy: this.state.groupBy || null,
            count: this.state.count || null,
            bindActions: this.state.bindActions,
            sort: this.state.sort,
            order: this.state.order,
            metrics: this.state.metrics || null,
            page: this.state.page,
            segments: this.state.segments && Object.keys(this.state.segments || {}).length ? JSON.stringify(this.state.segments) : null
        });
    }

    // Initial search: the filter is rendered only when the metrics are ready and calls onAction once on mount
    // -> exactly one request on page load (same as the other report pages)
    onSearch(typeAction) {
        this.setState({}, () => {
            this.props.search(this.state);
        });
    }

    onUpdateDate(index) {
        if (this.state.loadingData) {
            return;
        }

        const isStateChanged = this.isStateSearchChanged(this.state);

        this.setState({
            page: isStateChanged ? config.initialPage : this.state.page,
            ...defDataSort
        }, () => {
            this.onSearch();
            this.pushQuery();
        });
    }

    onActionFilter(filterState) {
        const resetValues = this.resetEcho;
        this.resetEcho = null;
        if (resetValues && isSameFilterValues(resetValues, filterState)) {
            return;
        }

        const isStateChanged = this.isStateSearchChanged(this.state);

        this.setState({
            page: isStateChanged ? config.initialPage : this.state.page,
            ...filterState
        }, () => {
            this.onSearch();
            this.pushQuery();
        });
    }

    // Reset all: top filters and filter rows to defaults, one request
    onResetAll() {
        const defaults = {
            startDate: moment().subtract(6, 'days').startOf('day'),
            endDate: moment().endOf('day'),
            bindActions: config.defaultBindActions,
            groupBy: config.defaultGroupByForReferral,
            count: config.defaultCount
        };

        this.onActionFilter({ ...defaults, segments: {}, page: config.initialPage, ...defDataSort });
        this.resetEcho = defaults;
    }

    onActionMetrics(metrics) {
        this.setState({
            metrics
        }, () => {
            this.onSearch();
            this.pushQuery();
        });
    }

    onActionTable(dataSort) {
        this.setState({
            ...dataSort
        }, () => {
            this.onSearch('table');
            this.pushQuery();
        });
    }

    onPageChange(page) {
        this.setState({
            page
        }, () => {
            this.onSearch('pagination');
            this.pushQuery();
        });
    }

    onActionSegments(segments) {
        this.setState({
            segments
        });
    }

    render() {
        if (!this.state.metrics) {
            return (
                <section className="wrapper-content statistics">
                    <div className="ibox text-center">
                        <Loading center />
                    </div>
                </section>
            );
        }

        return (
            <section className="wrapper-content statistics">
                <div className="ibox">
                    <StatisticsByReferralFilter
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        groupBy={this.state.groupBy}
                        count={this.state.count}
                        bindActions={this.state.bindActions}
                        onAction={this.onActionFilter}
                    />

                    <StatisticsByReferralSegmentsComponent
                        segments={this.state.segments}
                        onAction={this.onActionSegments}
                        onUpdateDate={this.onUpdateDate}
                        loading={this.state.loadingData}
                        onReset={this.onResetAll}
                        commonControls={true}
                    />
                </div>

                <div className="ibox-title bold" id="statistics-table-header">
                    <StatisticsByReferralMetricsComponent onAction={this.onActionMetrics} metrics={this.state.metrics}/>
                    <StatisticsByReferralCSV/>
                </div>

                <div className="ibox">
                    <div className="ibox-content no-padding">
                        <StatisticsByReferralTableComponent
                            sort={this.state.sort}
                            order={this.state.order}
                            metrics={this.state.metrics}
                            onAction={this.onActionTable}
                        />
                    </div>
                    <div className="ibox-footer pl0 pr0 pb0">
                        <Pagination
                            onPageChange={this.onPageChange}
                            initialPage={this.state.page}
                            totalPages={this.props.totalPages}
                            loading={this.state.loadingData}
                            itemsCountPerPage={this.state.count}
                            align={'left'}
                        />
                    </div>
                </div>
            </section>
        )
    };
}

StatisticsByReferralContent.propTypes = {
    metrics: PropTypes.string,
    segments: PropTypes.object,
    serverStorageReady: PropTypes.bool
};

StatisticsByReferralContent.defaultProps = {
    metrics: '',
    segments: {},
    serverStorageReady: false
};

StatisticsByReferralContent.contextTypes = {
    t: PropTypes.object
};

export default StatisticsByReferralContent;
